import React, { useState, useEffect } from "react";
import { postData, uploadFile, getData } from "../../../webService/webservice.js";
import { toast } from "react-toastify";
import TVFMLoader from "../Loader/loader.jsx";
import StoreMultiSelect from "../../Challange/storeMultiSelect.jsx";
import AddStoreForm from "../store/AddStoreForm.jsx";
import { Icon } from "@iconify/react";
import closeIcon from "@iconify/icons-mdi/close";
import { Typography } from "@mui/material";
import StockImagePicker from "../../../components/Common/ImagePicker/StockImagePicker.jsx";

const AddCategoryForm = ({ onSuccess, onCancel, initialStoreId }) => {
    const role = localStorage.getItem("role");
    const adminStoreIds = React.useMemo(() => JSON.parse(localStorage.getItem("storeIds") || "[]"), []);
    const [categoryImage, setCategoryImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    const [errors, setErrors] = useState({});
    const [stores, setStores] = useState([]);
    const [isAddStore, setIsAddStore] = useState(false);
    const [formData, setFormData] = useState({
        categoryName: '',
        categoryImageObject: '',
        storeId: initialStoreId || ""
    });

    const [uploadMethod, setUploadMethod] = useState("Normal"); // "normal" or "stock"
    const [showStockPicker, setShowStockPicker] = useState(false);
    const [selectedStockImage, setSelectedStockImage] = useState(null);

    useEffect(() => {
        // 🔒 Auto-select store for StoreAdmin with ONE store
        if ((role === "StoreAdmin" || role === "StoreManager") && adminStoreIds.length === 1) {
            const storeId = adminStoreIds[0];

            // Only fetch if not already loaded or correctly selected
            if (stores.length > 0 && stores[0].value === storeId) return;

            const fetchStoreName = async () => {
                try {
                    const token = localStorage.getItem("Token");
                    const res = await getData(`stores/store/${storeId}`, {}, token);

                    if (res?.data) {
                        setStores([
                            {
                                value: storeId,
                                label: res.data.storeName
                            }
                        ]);

                        setFormData(prev => ({
                            ...prev,
                            storeId: storeId
                        }));
                    }
                } catch (err) {
                    console.error("Failed to fetch store name", err);
                }
            };

            fetchStoreName();
        }
    }, [role, adminStoreIds, stores]);


    const loadStores = async (inputValue, loadedOptions, { page }) => {
        try {
            const token = localStorage.getItem("Token");
            const res = await getData(
                "stores/stores",
                { page, limit: 10, search: inputValue },
                token
            );

            const results = res?.data?.results || [];

            const options = results.map(store => ({
                value: store._id,
                label: store.storeName
            }));

            return {
                options,
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };

        } catch (err) {
            console.error("Store Fetch Failed", err);
            return { options: [], hasMore: false };
        }
    };

    const validateFields = (data, img, method, stockImg) => {
        let newErrors = {};
        if (!data.storeId) newErrors.storeId = "Store is required";
        if (method === "Normal" && img) {
            const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
            const allowedExtensions = ["jpg", "jpeg", "png"];
            const fileExtension = img.name.split('.').pop().toLowerCase();
            const isValidType = allowedTypes.includes(img.type) || allowedExtensions.includes(fileExtension);
            if (!isValidType) {
                newErrors.categoryImage = "Only JPEG, JPG, and PNG images are allowed.";
            } else if (img.size > 1024 * 1024) {
                newErrors.categoryImage = "Image size exceeds 1MB. Please select an image smaller than 1MB.";
            }
        }
        if (!data.categoryName?.trim()) newErrors.categoryName = "Category Name is required";
        return newErrors;
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();

        const validationErrors = validateFields(formData, categoryImage, uploadMethod, selectedStockImage);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});
        setShowLoader(true);

        try {
            const token = localStorage.getItem("Token");
            const userId = localStorage.getItem("userId");

            let finalImageObject = null;

            // ✅ NORMAL UPLOAD
            if (uploadMethod === "Normal") {
                if (categoryImage && userId) {
                    const uploadResult = await uploadFile(`files/upload/${userId}`, categoryImage);

                    if (uploadResult?.status === 200) {
                        finalImageObject = uploadResult.data;
                    } else {
                        toast.error(uploadResult?.message || "Image upload failed");
                        setShowLoader(false);
                        return;
                    }
                }
            }

            // ✅ STOCK IMAGE
            else {
                finalImageObject = selectedStockImage;
            }



            // ✅ FINAL PAYLOAD (🔥 IMPORTANT FIX)
            const categoryData = {
                categoryName: formData.categoryName,
                storeId: formData.storeId,
                categoryImageObject: finalImageObject,

                // 🔥 THIS LINE FIXES YOUR ISSUE
                imageSource: uploadMethod === "Stock" ? "Stock" : "Upload"
            };

            console.log("Add Category Payload:", categoryData);

            const response = await postData("categories/", categoryData, token);

            toast.success(response.message || "Category added successfully");

            // ✅ RESET FORM
            setFormData({
                categoryName: "",
                storeId: "",
                categoryImageObject: null
            });

            setCategoryImage(null);
            setSelectedStockImage(null);
            setPreviewUrl(null);
            setUploadMethod("Normal");

            onSuccess(response.data);

        } catch (error) {
            console.error("Add Category Error:", error);
            toast.error(error.message || "Add Category Failed");
        } finally {
            setShowLoader(false);
        }
    };

    const handleRemoveImage = () => {
        setPreviewUrl(null);
        setCategoryImage(null);
        setSelectedStockImage(null);
    };

    const handleStockSelect = (imageObject) => {
        if (!imageObject) return;
        setSelectedStockImage(imageObject);
        const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
        const uri = imageObject.uri || imageObject.fileObject?.uri || "";
        if (uri) {
            setPreviewUrl(`${baseUrl}/${uri.replace(/^\/+/, "")}`);
        }
        setShowStockPicker(false);
        setErrors(prev => ({ ...prev, categoryImage: null }));
    };

    return (
        <div className="add-category-popup-form">
            {showLoader && (
                <div className="scissor-overlay">
                    <div className="scissor-loader"><TVFMLoader /></div>
                </div>
            )}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Add Category
            </Typography>
            <form className="Product-form" onSubmit={handleAddCategory}>
                <div className="category-form-row">
                    <div className="category-form-group">
                        <label className="input-label">Store Name</label>
                        <StoreMultiSelect
                            value={stores}
                            onChange={(val) => {
                                // ❌ Prevent change for StoreAdmin with one store
                                if (
                                    (role === "StoreAdmin" || role === "StoreManager") &&
                                    adminStoreIds.length === 1
                                ) return;

                                setStores(val);
                                setFormData(prev => ({
                                    ...prev,
                                    storeId: val && val.length > 0 ? val[0].value : ""
                                }));
                            }}
                            loadStores={loadStores}
                            loggedInRole={role}
                            assignedStoreIds={adminStoreIds}
                            isMulti={false}
                            isDisabled={
                                (role === "StoreAdmin" || role === "StoreManager") &&
                                adminStoreIds.length === 1
                            }
                            onAddStore={() => setIsAddStore(true)}
                        />

                        {errors.storeId && <p className="field-error">{errors.storeId}</p>}
                    </div>
                    <div className="category-form-group">
                        <label htmlFor="categoryName" className="input-label">Category Name</label>
                        <input
                            type="text"
                            placeholder="Enter category name"
                            className="category-input"
                            value={formData.categoryName}
                            onChange={e => setFormData(prev => ({ ...prev, categoryName: e.target.value }))}
                        />
                        {errors.categoryName && <p className="field-error">{errors.categoryName}</p>}
                    </div>

                    <div className="category-form-group" style={{ gridColumn: 'span 2' }}>
                        <label className="input-label">Category Image (Max 1MB, JPEG/JPG/PNG only)</label>
                        <div className="upload-options" style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#525252' }}>
                                <input
                                    type="radio"
                                    name="uploadMethod"
                                    checked={uploadMethod === "Normal"}
                                    style={{ accentColor: 'linear-gradient(90deg, #181B23 0%, #1C202B 35%, #1A1E28 65%, #161A22 100%)' }}
                                    onChange={() => {
                                        setUploadMethod("Normal");
                                        setPreviewUrl(categoryImage ? URL.createObjectURL(categoryImage) : null);
                                    }}
                                />
                                Normal Upload
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#525252' }}>
                                <input
                                    type="radio"
                                    name="uploadMethod"
                                    checked={uploadMethod === "Stock"}
                                    style={{ accentColor: 'linear-gradient(90deg, #181B23 0%, #1C202B 35%, #1A1E28 65%, #161A22 100%)' }}
                                    onChange={() => {
                                        setUploadMethod("Stock");
                                        if (selectedStockImage) {
                                            const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
                                            const uri = selectedStockImage.uri || selectedStockImage.fileObject?.uri || "";
                                            setPreviewUrl(`${baseUrl}/${uri.replace(/^\/+/, "")}`);
                                        } else {
                                            setPreviewUrl(null);
                                        }
                                    }}
                                />
                                Stock Image
                            </label>
                        </div>

                        {uploadMethod === "Normal" ? (
                            <input
                                type="file"
                                accept=".jpeg,.jpg,.png"
                                className="category-input"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
                                        const allowedExtensions = ["jpg", "jpeg", "png"];
                                        const fileExtension = file.name.split('.').pop().toLowerCase();
                                        const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);

                                        if (!isValidType) {
                                            setErrors(prev => ({ ...prev, categoryImage: "Only JPEG, JPG, and PNG images are allowed." }));
                                            toast.error("Only JPEG, JPG, and PNG images are allowed.");
                                            setCategoryImage(null);
                                            setPreviewUrl(null);
                                            if (e.target) e.target.value = "";
                                        } else if (file.size > 1024 * 1024) {
                                            setErrors(prev => ({ ...prev, categoryImage: "Image size exceeds 1MB. Please select an image smaller than 1MB." }));
                                            toast.error("Image size exceeds 1MB. Please select an image smaller than 1MB.");
                                            setCategoryImage(null);
                                            setPreviewUrl(null);
                                            if (e.target) e.target.value = "";
                                        } else {
                                            setCategoryImage(file);
                                            setPreviewUrl(URL.createObjectURL(file));
                                            setErrors(prev => ({ ...prev, categoryImage: null }));
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <div className="stock-picker-trigger">
                                <button
                                    type="button"
                                    className="select-stock-btn"
                                    onClick={() => setShowStockPicker(true)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        background: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        textAlign: 'left',
                                        justifyContent: 'flex-start',
                                        gap: '10px',
                                        fontSize: '14px',
                                        color: selectedStockImage ? '#333' : '#888'
                                    }}
                                >
                                    <Icon icon="mdi:image-search" style={{ fontSize: '18px', color: '#64748b' }} />
                                    {selectedStockImage ? `Selected: ${selectedStockImage.name || 'Stock Image'}` : "Click to select a stock image..."}
                                </button>
                            </div>
                        )}
                        {errors.categoryImage && <p className="field-error">{errors.categoryImage}</p>}
                        {previewUrl && (
                            <div className="image-preview" style={{ marginTop: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                <div style={{ position: 'relative' }}>
                                    <img src={previewUrl} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }} />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            background: '#ef4444',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            zIndex: 5
                                        }}
                                        onClick={handleRemoveImage}
                                        title="Remove image"
                                    >
                                        <Icon icon="mdi:close" style={{ fontSize: '14px' }} />
                                    </div>
                                </div>
                                {uploadMethod === "Stock" && selectedStockImage && (
                                    <div style={{ paddingTop: '4px' }}>
                                        <p style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', margin: '0' }}>Stock Image Selected</p>
                                        <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0' }}>{selectedStockImage.name}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="category-buttons">
                    <button className="add-Product-button" type="submit">Add Category</button>
                    <button className="cancel-Product-button" type="button" onClick={onCancel}>Cancel</button>
                </div>
            </form>
            {isAddStore && (
                <div className="popup-overlay">
                    <div className="popup-wrapper" style={{ maxWidth: '900px', width: '95%' }}>
                        <Icon
                            icon={closeIcon}
                            className="popup-close-icon-outside"
                            onClick={() => setIsAddStore(false)}
                        />
                        <div className="popup-form" style={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px' }}>
                            <AddStoreForm
                                onSuccess={() => {
                                    setIsAddStore(false);
                                }}
                                onCancel={() => setIsAddStore(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
            {showStockPicker && (
                <StockImagePicker
                    onSelect={handleStockSelect}
                    onCancel={() => setShowStockPicker(false)}
                />
            )}
        </div>

    );
};

export default AddCategoryForm;
